import React from "react";
import { i18n } from "../../services";
import { EVT_MOUSEDOWN } from "../../lib";
import styled from "styled-components";
import { isPointInRect } from "../../helpers";

const Container = styled.div<{ fullWidth: boolean }>`
    position: relative;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
`;

const Placeholder = styled.div<{ margin: string }>`
    margin: ${(p) => p.margin};
    font-family: urdu;
    font-weight: 500;
    font-size: 12px;
`;

const Value = styled.div<{ fullWidth: boolean; open: boolean }>`
    user-select: none;
    border: 0;
    border-bottom: 1px solid #dcdbdc;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
    outline: none;
    font-family: urdu;
    font-weight: 500;
    font-size: 18px;

    &:hover {
        border-bottom-color: ${(p) => (p.open ? "#dcdbdc" : "#000")};
    }

    &:after {
        display: ${(p) => (p.open ? "none" : "block")};
        position: absolute;
        content: " ";
        left: 10px;
        bottom: 13px;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #000;
    }

    &:before {
        display: ${(p) => (p.open ? "block" : "none")};
        position: absolute;
        content: " ";
        left: 10px;
        bottom: 13px;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-bottom: 5px solid #000;
    }
`;

const List = styled.div<{ top: number; fullWidth: boolean; open: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    z-index: 50;
    position: absolute;
    display: ${(p) => (p.open ? "block" : "none")};
    top: ${(p) => p.top}px;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
    max-height: 250px;
    overflow-x: hidden;
    overflow-y: auto;
    color: #fff;
    background-color: #000;
`;

const Item = styled.div<{ isActive: boolean; fullWidth: boolean }>`
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    user-select: none;
    padding: 3px 0;
    cursor: ${(p) => (p.isActive ? "not-allowed" : "default")};
    pointer-events: ${(p) => (p.isActive ? "none" : "auto")};
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
    background-color: ${(p) => (p.isActive ? "#333" : "transparent")};

    &:hover {
        background-color: #333;
    }
`;

const Text = styled.div<{ hasIndent: boolean }>`
    font-family: urdu;
    font-weight: 500;
    padding-right: ${(p) => (p.hasIndent ? 10 : 15)}px;
`;

const INDENT_OFFSET = 36;
const INDENT_PADDING = 15;

const IndentMark = styled.div<{ amount: number }>`
    position: relative;
    margin-right: ${(p) => (p.amount - 1) * INDENT_OFFSET + INDENT_PADDING}px;
    width: ${INDENT_OFFSET}px;
    border-bottom: 1px solid #fff;
    margin-bottom: 10px;
`;

interface SelectProps {
    id: string;
    name: string;
    value: string;
    labels: string[];
    values: string[];
    margin?: string;
    fullWidth: boolean;
    placeholder?: string;
    indents?: Array<number>;

    onChange: (name: string, value: string) => void;
}
interface SelectState {
    open: boolean;
}
export class Select extends React.Component<SelectProps, SelectState> {
    private fieldValue: React.RefObject<HTMLDivElement>;
    private elmList: React.RefObject<HTMLDivElement>;

    constructor(props: SelectProps) {
        super(props);

        this.fieldValue = React.createRef<HTMLDivElement>();
        this.elmList = React.createRef<HTMLDivElement>();

        this.state = {
            open: false,
        };
    }

    componentDidMount() {
        window.addEventListener(EVT_MOUSEDOWN, this.onMouseDown);
    }

    componentWillUnmount() {
        window.removeEventListener(EVT_MOUSEDOWN, this.onMouseDown);
    }

    onMouseDown = (evt: MouseEvent) => {
        if (!this.state.open || !this.elmList) {
            return;
        }

        const c = this.elmList.current;
        if (!c) {
            return;
        }

        if (
            !isPointInRect(
                { x: evt.clientX, y: evt.clientY },
                c.getBoundingClientRect()
            )
        ) {
            setTimeout(() => this.setState({ open: false }), 250);
        }
    };

    toggleOpen = () => this.setState({ open: !this.state.open });

    onSelect = (v: string) => {
        this.props.onChange(this.props.name, v);
        this.setState({ open: false });
    };

    list = () => {
        const {
            id,
            name,
            value,
            values,
            labels,
            indents,
            fullWidth,
        } = this.props;

        return values.map((v, i) => {
            const relativeIndex = values.indexOf(v);
            const indent =
                typeof indents !== "undefined" ? indents[relativeIndex] : 0;
            const label =
                typeof labels !== "undefined"
                    ? labels[i]
                    : i18n.get(`${id}Form.${name}${v}`);

            return (
                <Item
                    key={`${name}${v}`}
                    fullWidth={fullWidth}
                    isActive={value === v}
                    onClick={() => this.onSelect(v)}
                >
                    {indent > 0 && <IndentMark amount={indent} />}
                    <Text hasIndent={indent > 0}>{label}</Text>
                </Item>
            );
        });
    };

    render() {
        const {
            id,
            name,
            value,
            labels,
            values,
            margin,
            fullWidth,
            placeholder,
        } = this.props;

        const valueLabel =
            typeof labels !== "undefined"
                ? labels[values.indexOf(value)]
                : i18n.get(`${id}Form.${name}${value}`);

        let listTop = 0;
        const f = this.fieldValue;
        if (!!f && !!f.current) {
            listTop = f.current.offsetTop + f.current.offsetHeight;
        }

        return (
            <Container fullWidth={fullWidth}>
                <Placeholder margin={margin || "40px 0 10px 0"}>
                    {placeholder}
                </Placeholder>
                <Value
                    ref={this.fieldValue}
                    fullWidth={fullWidth}
                    open={this.state.open}
                    onClick={this.toggleOpen}
                >
                    {valueLabel}
                </Value>
                <List
                    ref={this.elmList}
                    top={listTop}
                    fullWidth={fullWidth}
                    open={this.state.open}
                >
                    {this.list()}
                </List>
            </Container>
        );
    }
}
